/**
 * Toolbar Customization Store
 * Photoshop-style toolbar personalization: which items the toolbar shows and
 * in what order, plus which face each group remembers. Everything persists
 * under the panel storage namespace so a customized layout survives restarts.
 *
 * The customization state is expressed as item ids only (group ids for tool
 * groups, fixed ids for the bottom accessories); the canonical item definitions
 * (icons, labels, ordering) live in the components so ids stay a stable key.
 *
 * Lazy singleton — see useDockLayoutStore for the namespace rationale.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { debouncedLocalStorage } from './persistStorage';
import { panelStorageKey } from './namespace';

/** Bottom accessory rows rendered under the tool groups (app declaration) */
export type ToolbarAccessoryId = string;

/** Any toolbar row addressable by the customize menu */
export type ToolbarItemId = string; // group ids + ToolbarAccessoryId

interface ToolbarCustomizationState {
    /** Visible items in display order (top to bottom) */
    visibleItems: ToolbarItemId[];
    /** Hidden items in their original definition order (menu checkmarks off) */
    hiddenItems: ToolbarItemId[];
    /** Layout lock: while true, drag-to-reorder is disabled everywhere */
    locked: boolean;
    setItemVisibility: (itemId: ToolbarItemId, visible: boolean) => void;
    setVisibleOrder: (order: ToolbarItemId[]) => void;
    setLocked: (locked: boolean) => void;
    resetToolbarCustomization: () => void;
}

type ToolbarCustomizationStoreApi = ReturnType<
    typeof createToolbarCustomizationStore
>;

/**
 * Default layout = the exact definition order shipped with the app. Computed
 * lazily from the flat id list the toolbar registers, so new tools added to
 * the definition arrays automatically join the default order.
 */
let DEFAULT_ORDER: ToolbarItemId[] = [];

export const registerDefaultToolbarOrder = (
    ids: ToolbarItemId[],
): void => {
    if (DEFAULT_ORDER.length === 0 && ids.length > 0) {
        DEFAULT_ORDER = [...ids];
    }
};

export const getDefaultToolbarOrder = (): ToolbarItemId[] => [
    ...DEFAULT_ORDER,
];

const defaultState = {
    visibleItems: [] as ToolbarItemId[],
    hiddenItems: [] as ToolbarItemId[],
    locked: false,
};

const createToolbarCustomizationStore = () =>
    create<ToolbarCustomizationState>()(
        persist(
            (set, get) => ({
                ...defaultState,

                setItemVisibility: (itemId, visible) => {
                    const { visibleItems, hiddenItems } = get();
                    if (visible) {
                        // Restore: re-insert at the item's default neighborhood —
                        // before the first visible item that follows it in the
                        // default order, else at the end. Custom order of the
                        // already-visible items is preserved.
                        const defaultIndex = DEFAULT_ORDER.indexOf(itemId);
                        const next = [...visibleItems];
                        let insertAt = next.length;
                        if (defaultIndex >= 0) {
                            for (let i = defaultIndex + 1; i < DEFAULT_ORDER.length; i++) {
                                const position = next.indexOf(DEFAULT_ORDER[i]);
                                if (position >= 0) {
                                    insertAt = position;
                                    break;
                                }
                            }
                        }
                        next.splice(insertAt, 0, itemId);
                        set({
                            visibleItems: next,
                            hiddenItems: hiddenItems.filter((id) => id !== itemId),
                        });
                    } else {
                        set({
                            hiddenItems: [...hiddenItems, itemId],
                            visibleItems: visibleItems.filter(
                                (id) => id !== itemId,
                            ),
                        });
                    }
                },

                setVisibleOrder: (order) => set({ visibleItems: order }),

                setLocked: (locked) => set({ locked }),

                resetToolbarCustomization: () => set({ ...defaultState }),
            }),
            {
                name: panelStorageKey('toolbar_customization'),
                storage: createJSONStorage(() => debouncedLocalStorage()),
                partialize: (state) => ({
                    visibleItems: state.visibleItems,
                    hiddenItems: state.hiddenItems,
                    locked: state.locked,
                }),
            },
        ),
    );

let instance: ToolbarCustomizationStoreApi | null = null;
const getStore = (): ToolbarCustomizationStoreApi =>
    (instance ??= createToolbarCustomizationStore());

/** Namespace-lazy singleton: plain hook + static accessor shape preserved. */
export const useToolbarCustomizationStore: ToolbarCustomizationStoreApi =
    ((...args: unknown[]) =>
        (getStore() as unknown as (...a: unknown[]) => unknown)(...args)) as ToolbarCustomizationStoreApi;
useToolbarCustomizationStore.getState = () => getStore().getState();
useToolbarCustomizationStore.setState = ((...args: unknown[]) =>
    (
        getStore().setState as unknown as (...a: unknown[]) => unknown
    )(...args)) as ToolbarCustomizationStoreApi['setState'];
useToolbarCustomizationStore.subscribe = ((...args: unknown[]) =>
    (
        getStore().subscribe as unknown as (...a: unknown[]) => unknown
    )(...args)) as ToolbarCustomizationStoreApi['subscribe'];
